/**
 * ADR-122 — how the Emails page shows the details the platform fills in.
 *
 * <p>The engine carries an email's wording with each merge field as a `{{field}}` token. Staff are
 * accountants, not developers, so the editor never shows a token: each one is written as its label
 * in square brackets — `[Client name]`, `[Form link]` — and drawn as a grey box. On the way back to
 * the engine every `[Label]` that names one of THIS email's details becomes its token again; any other
 * bracketed text is left exactly as typed (people write brackets).</p>
 *
 * <p>Labels come from the engine (the declaration's `field_labels`, else the field name in words), and
 * the deploy lint holds them unique per email (ARD-DSL-079), so the mapping is one-to-one.</p>
 */
import type { EmailTemplateField } from "../api/types.js";

/**
 * A merge token, optionally piped through one of the platform's display filters
 * (`{{ party_name | plain_name }}`, card #2933). The editor shows either as the field's label; the
 * engine gives a token sent back bare the filter the default wording uses for that field, so a
 * save keeps the default's presentation.
 */
const TOKEN = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:\|\s*[a-z_]+\s*)?\}\}/g;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The engine's wording as the editor shows it: `{{form_url}}` → `[Form link]`. */
export function toEditor(text: string, fields: readonly EmailTemplateField[]): string {
  const byName = new Map(fields.map((f) => [f.name, f.label]));
  return text.replace(TOKEN, (whole, name: string) => {
    const label = byName.get(name);
    return label === undefined ? whole : `[${label}]`;
  });
}

/** The editor's wording as the engine takes it: `[Form link]` → `{{form_url}}`. */
export function fromEditor(text: string, fields: readonly EmailTemplateField[]): string {
  if (fields.length === 0) return text;
  // Longest label first, so a label that is a prefix of another can never claim it.
  const sorted = [...fields].sort((a, b) => b.label.length - a.label.length);
  const pattern = new RegExp(`\\[(${sorted.map((f) => escapeRegExp(f.label)).join("|")})\\]`, "g");
  const byLabel = new Map(fields.map((f) => [f.label, f.name]));
  return text.replace(pattern, (whole, label: string) => {
    const name = byLabel.get(label);
    return name === undefined ? whole : `{{${name}}}`;
  });
}

/** One run of the editor's text: plain words, or one of the email's details. */
export type WordingPiece = { kind: "text"; text: string } | { kind: "field"; text: string; field: EmailTemplateField };

/** The editor's text cut into plain runs and detail boxes — what the highlight layer draws. */
export function pieces(text: string, fields: readonly EmailTemplateField[]): WordingPiece[] {
  if (fields.length === 0 || text === "") return text === "" ? [] : [{ kind: "text", text }];
  const sorted = [...fields].sort((a, b) => b.label.length - a.label.length);
  const pattern = new RegExp(`\\[(${sorted.map((f) => escapeRegExp(f.label)).join("|")})\\]`, "g");
  const byLabel = new Map(fields.map((f) => [f.label, f]));
  const out: WordingPiece[] = [];
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    const index = m.index ?? 0;
    if (index > last) out.push({ kind: "text", text: text.slice(last, index) });
    out.push({ kind: "field", text: m[0], field: byLabel.get(m[1]!)! });
    last = index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** The required details (the link) the editor's text no longer carries. */
export function missingRequired(text: string, fields: readonly EmailTemplateField[]): EmailTemplateField[] {
  return fields.filter((f) => f.required && !text.includes(`[${f.label}]`));
}

/** "6 Oct 2026 at 14:05" — when an email was last changed, in the reader's own time zone. */
export function editedWhen(iso: string | null): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date} at ${time}`;
}
